import type {
  ChangedFieldPathToken,
  ChangeTrackingSource,
} from '@typeDefinitions/changeTrackingTypes';
import type { ICustomerDetails } from '@store/customers/customerDetails/types';

import { createFormChangeTracker } from '../../utils/createFormChangeTracker';
import { CHANGE_TRACKING_SOURCES } from '../../utils/changeTrackingRegistry';
import { findChangedForms } from '../../utils/findChangedForms';

const IGNORED_FIELDS = new Set([
  'createdDate',
  'operationType',
  'financials',
  'outstandingOrders',
  'orderSummary',
  'pendingCreditRequests',
  'requests',
]);

const COLLECTION_FIELDS = new Set([
  'collectionComments',
  'collectionsUserId',
  'collectionTierId',
  'nextActionDate',
  'keyReasonId',
]);

const GENERAL_CUSTOMER_INFORMATION_FIELDS = new Set([
  'accountingSysId',
  'alwaysTrackLoads',
  'bolInstructions',
  'businessTypeCode',
  'businessTypeDisplayName',
  'businessTypeId',
  'businessTypeName',
  'customerTier',
  'fuelReimbursement',
  'loadInstructions',
  'name',
  'optOutBalanceEmail',
  'optOutPastDueDateBalanceEmail',
  'pcMilerVersion',
  'pointOfSaleInstructions',
  'status',
  'subStatus',
  'trackingServiceId',
  'trackingVisibilityProvider',
  'trackingXId',
]);

const TRANSPORTATION_FIELDS = new Set([
  'assetBased',
  'isAuthorityCarrier',
  'isAuthorityDirectShipper',
  'isAuthorityFreightBroker',
  'isAuthorityFreightForwarder',
  'isCrossBorder',
  'mcNumber',
]);

const GENERAL_DO_NOT_SOLICIT_FIELDS = new Set([
  'approvedCustomerDoNotSolicitDate',
  'isApprovedCustomerDoNotSolicit',
  'requestedCustomerDoNotSolicit',
]);

const GENERAL_OWNERSHIP_FIELDS = new Set([
  'accountManagerEmail',
  'accountManagerId',
  'accountManagerName',
  'accountManagerOfficeAccountingGroupName',
  'accountManagerOfficeId',
  'accountManagerOfficeName',
  'accountManagerPhone',
  'capacityGroupId',
  'claimedBy',
  'claimedById',
  'groups',
  'isPublic',
  'isPublicEditable',
  'operationsRepId',
  'operationsRepName',
  'operationsRepOfficeId',
  'salesManagerEmail',
  'salesManagerId',
  'salesManagerName',
  'salesManagerOfficeAccountingGroupName',
  'salesManagerOfficeId',
  'salesManagerOfficeName',
  'salesManagerPhone',
]);

const FINANCIAL_CREDIT_TAX_FIELDS = new Set([
  'absoluteCreditThreshold',
  'creditInsuranceCoverageType',
  'creditInsuranceCoverageTypeName',
  'creditInsuranceId',
  'creditInsurancePolicyDate',
  'creditLimit',
  'insuredFor',
  'negativeCreditAllowance',
  'taxId',
]);

const FINANCIAL_INSURANCE_FIELDS = new Set([
  'agentLiability',
  'agentLiabilityEffectiveDate',
  'agentLiabilityNote',
  'atgLiability',
  'insurance',
]);

const FINANCIAL_INVOICING_FIELDS = new Set([
  'billingInstructions',
  'chargeCreditCardFee',
  'collectionTerms',
  'exceptionReason',
  'holdRetraction',
  'invoiceMethod',
  'invoicePaperworkRequired',
  'needsPreapproval',
  'paymentTerms',
  'requireEmptyBeforeProcessingLoad',
  'revisitRetractionDate',
  'tmsPortalViewModel',
  'verified',
]);

const FINANCIAL_ADDRESS_FIELDS = new Set([
  'mailingAddress1',
  'mailingAddress2',
  'mailingCity',
  'mailingCountryCode',
  'mailingState',
  'mailingZip',
  'physicalAddress1',
  'physicalAddress2',
  'physicalCity',
  'physicalCountryCode',
  'physicalState',
  'physicalZip',
]);

const FINANCIAL_ADDITIONAL_DETAILS_FIELDS = new Set([
  'emailDomain',
  'isCustomerCentralized',
  'seasonality',
  'seasonalityComment',
]);

const isEmptyFormValue = (value: unknown): boolean =>
  value === null ||
  value === undefined ||
  value === '' ||
  (Array.isArray(value) && value.length === 0);

const getPropertyPath = (path: readonly ChangedFieldPathToken[]): string[] =>
  path.flatMap((token) => (token.type === 'property' ? [token.key] : []));

const areCustomerScalarValuesEqual = (
  before: unknown,
  after: unknown,
  path: readonly ChangedFieldPathToken[],
): boolean => {
  if (Object.is(before, after)) return true;
  if (isEmptyFormValue(before) && isEmptyFormValue(after)) return true;

  if (before instanceof Date && after instanceof Date) {
    return before.getTime() === after.getTime();
  }

  if (typeof before === 'number' && typeof after === 'string') {
    return after.trim() !== '' && Number(after) === before;
  }

  if (typeof before === 'string' && typeof after === 'number') {
    return before.trim() !== '' && Number(before) === after;
  }

  const field = getPropertyPath(path).at(-1) ?? '';
  if (
    /phone/i.test(field) &&
    typeof before === 'string' &&
    typeof after === 'string'
  ) {
    return before.replace(/\D/g, '') === after.replace(/\D/g, '');
  }

  return false;
};

const getCustomerDetailsForm = (
  path: readonly ChangedFieldPathToken[],
): ChangeTrackingSource => {
  const field = getPropertyPath(path).at(0);
  if (field === undefined)
    return CHANGE_TRACKING_SOURCES.customerDetails.general;

  if (COLLECTION_FIELDS.has(field)) {
    return CHANGE_TRACKING_SOURCES.customerDetails.collections;
  }

  if (field === 'comments') {
    return CHANGE_TRACKING_SOURCES.customerDetails.generalComments;
  }

  if (field === 'contacts') {
    return CHANGE_TRACKING_SOURCES.customerDetails.generalContacts;
  }

  if (field === 'defaultLogoUrl') {
    return CHANGE_TRACKING_SOURCES.customerDetails.generalUploadDocuments;
  }

  if (field === 'flagEmptyLoads') {
    return CHANGE_TRACKING_SOURCES.customerDetails.generalEmptyLoadReview;
  }

  if (field === 'metadata') {
    return CHANGE_TRACKING_SOURCES.customerDetails.headerCustomFields;
  }

  if (field === 'requiredDocumentTypes') {
    return CHANGE_TRACKING_SOURCES.customerDetails.financialsRequiredDocuments;
  }

  if (TRANSPORTATION_FIELDS.has(field)) {
    return CHANGE_TRACKING_SOURCES.customerDetails.generalTransportation;
  }

  if (GENERAL_CUSTOMER_INFORMATION_FIELDS.has(field)) {
    return CHANGE_TRACKING_SOURCES.customerDetails.generalCustomerInformation;
  }

  if (GENERAL_DO_NOT_SOLICIT_FIELDS.has(field)) {
    return CHANGE_TRACKING_SOURCES.customerDetails.generalDoNotSolicit;
  }

  if (GENERAL_OWNERSHIP_FIELDS.has(field)) {
    return CHANGE_TRACKING_SOURCES.customerDetails.generalOwnership;
  }

  if (FINANCIAL_CREDIT_TAX_FIELDS.has(field)) {
    return CHANGE_TRACKING_SOURCES.customerDetails.financialsCreditTax;
  }

  if (FINANCIAL_INSURANCE_FIELDS.has(field)) {
    return CHANGE_TRACKING_SOURCES.customerDetails.financialsInsurance;
  }

  if (FINANCIAL_INVOICING_FIELDS.has(field)) {
    return CHANGE_TRACKING_SOURCES.customerDetails.financialsInvoicing;
  }

  if (FINANCIAL_ADDRESS_FIELDS.has(field)) {
    return CHANGE_TRACKING_SOURCES.customerDetails.financialsAddresses;
  }

  if (FINANCIAL_ADDITIONAL_DETAILS_FIELDS.has(field)) {
    return CHANGE_TRACKING_SOURCES.customerDetails.financialsAdditionalDetails;
  }

  return CHANGE_TRACKING_SOURCES.customerDetails.generalCustomerInformation;
};

const findCustomerDetailsChangedForms = (
  before: ICustomerDetails,
  after: ICustomerDetails,
): ChangeTrackingSource[] =>
  findChangedForms(before, after, {
    ignoredFields: IGNORED_FIELDS,
    areScalarValuesEqual: areCustomerScalarValuesEqual,
    getForm: (change) => getCustomerDetailsForm(change.path),
  });

const customerDetailsFormChangeTracker = createFormChangeTracker<
  ICustomerDetails,
  ChangeTrackingSource
>({
  getIdentity: (customer) => customer.id ?? undefined,
  findChangedForms: findCustomerDetailsChangedForms,
});

export const setCustomerDetailsChangeBaseline =
  customerDetailsFormChangeTracker.setBaseline;
export const synchronizeCustomerDetailsChangeHydration =
  customerDetailsFormChangeTracker.synchronizeHydration;
export const prepareCustomerDetailsChangeSummary =
  customerDetailsFormChangeTracker.prepare;
